import { useState } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/index.js';
import { PasswordInput } from '../auth/PasswordInput.jsx';

export function Login() {
  const { signIn, signUp, confirmSignUp, resetPassword, confirmResetPassword, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState(() => searchParams.get('mode') || 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && isAuthenticated) return <Navigate to="/dashboard" replace />;

  function switchMode(next) {
    setMode(next);
    setError('');
    setSuccess('');
    setPassword('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
  }

  async function handleSignIn(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signUp(email, password);
      setMode('confirm');
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmSignUp(email, code);
      await signIn(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await resetPassword(email);
    } catch (err) {
      // Don't reveal whether user exists — silently proceed
      if (!err.message?.includes('UserNotFoundException')) {
        setError(friendlyError(err));
        setSubmitting(false);
        return;
      }
    }
    setMode('reset');
    setSuccess('If an account exists for this email, a reset code has been sent.');
    setSubmitting(false);
  }

  async function handleResendCode() {
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await resetPassword(email);
      setSuccess('New code sent! Check your email.');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmResetPassword(email, code, newPassword);
      await signIn(email, newPassword);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function friendlyError(err) {
    const msg = err.message || '';
    if (msg.includes('LimitExceededException') || msg.includes('limit exceeded'))
      return 'Too many attempts. Please wait a few minutes and try again.';
    if (msg.includes('ExpiredCodeException') || msg.includes('expired'))
      return 'Code has expired. Please request a new one.';
    if (msg.includes('CodeMismatchException') || msg.includes('Invalid verification'))
      return 'Invalid code. Please check and try again.';
    if (msg.includes('UserNotFoundException'))
      return 'If an account exists for this email, a code has been sent.';
    if (msg.includes('InvalidPasswordException') || msg.includes('Password did not conform'))
      return 'Password does not meet requirements.';
    return msg || 'Something went wrong. Please try again.';
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <h1 className="text-2xl font-bold text-center">Launchpad</h1>

        {error && (
          <p className="text-sm text-destructive-600 bg-destructive-500/10 rounded px-3 py-2">
            {error}
          </p>
        )}

        {success && (
          <p className="text-sm text-green-700 bg-green-500/10 rounded px-3 py-2">
            {success}
          </p>
        )}

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Signing in…' : 'Sign In'}
            </button>
            <p className="text-center text-sm text-slate-600">
              No account?{' '}
              <button type="button" onClick={() => switchMode('signup')} className="text-primary-600 hover:underline">
                Sign up
              </button>
            </p>
            <p className="text-center text-sm text-slate-600">
              <button type="button" onClick={() => switchMode('forgot')} className="text-primary-600 hover:underline">
                Forgot password?
              </button>
            </p>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              showRules
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Creating account…' : 'Sign Up'}
            </button>
            <p className="text-center text-sm text-slate-600">
              Have an account?{' '}
              <button type="button" onClick={() => switchMode('signin')} className="text-primary-600 hover:underline">
                Sign in
              </button>
            </p>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirm} className="space-y-4">
            <p className="text-sm text-slate-600">Check your email for a verification code.</p>
            <input
              type="text"
              placeholder="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="w-full rounded border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot} className="space-y-4">
            <p className="text-sm text-slate-600">Enter your email to receive a reset code.</p>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send Reset Code'}
            </button>
            <p className="text-center text-sm text-slate-600">
              <button type="button" onClick={() => switchMode('signin')} className="text-primary-600 hover:underline">
                Back to sign in
              </button>
            </p>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={handleReset} className="space-y-4">
            <p className="text-sm text-slate-600">Enter the code from your email and your new password.</p>
            <input
              type="text"
              placeholder="Reset code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="one-time-code"
              inputMode="numeric"
              className="w-full rounded border border-slate-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              showRules
              required
            />
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Resetting…' : 'Reset Password'}
            </button>
            <p className="text-center text-sm text-slate-600">
              Didn't get the code?{' '}
              <button type="button" onClick={handleResendCode} disabled={submitting} className="text-primary-600 hover:underline disabled:opacity-50">
                Resend
              </button>
            </p>
            <p className="text-center text-sm text-slate-600">
              <button type="button" onClick={() => switchMode('signin')} className="text-primary-600 hover:underline">
                Back to sign in
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
